import React, { useState } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '../components/auth/AuthContext';
import { Logo } from '../components/layout/Logo';


export const LoginPage: React.FC = () => {
    const { login } = useAuth();
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const handleLoginSuccess = async (tokenResponse: any) => {
        setLoading(true);
        setError(null);

        try {
            const accessToken = tokenResponse.access_token;

            // Call n8n Webhook for Backend Authorization
            const response = await fetch('/api/admin/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({ accessToken })
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Full server error response:', errorText);

                if (response.status === 401 || response.status === 403) {
                    throw new Error('אימייל זה אינו מורשה גישה במערכת זו.');
                }
                throw new Error(`שגיאת שרת (${response.status}) בהתחברות למערכת.`);
            }

            const userData = await response.json();

            // Save the session using validated data from n8n
            login(accessToken, userData);

        } catch (err: any) {
            console.error('Login error:', err);
            setError(err.message || 'אירעה שגיאה בתהליך ההתחברות. אנא נסה שוב.');
        } finally {
            setLoading(false);
        }
    };

    const googleLogin = useGoogleLogin({
        onSuccess: handleLoginSuccess,
        onError: () => setError('התחברות ל-Google נכשלה. אנא נסה שוב.'),
        prompt: 'select_account',
    });

    return (
        <div className="login-page-container">
            {/* Background Watermark */}
            <div className="login-watermark">
                <Logo style={{ width: '100%', height: '100%' }} />
            </div>

            <div className="login-card-premium">
                <div style={{ marginBottom: 'var(--space-8)' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-6)' }}>
                        <Logo height={64} style={{ filter: 'brightness(0) saturate(100%) invert(13%) sepia(21%) saturate(2283%) hue-rotate(136deg) brightness(98%) contrast(92%)' }} />
                    </div>
                    <div>
                        <div style={{
                            fontSize: 'var(--font-size-4xl)',
                            fontWeight: 'var(--font-weight-black)',
                            color: 'var(--primary-600)',
                            marginBottom: 'var(--space-1)',
                            letterSpacing: '-0.02em',
                            textTransform: 'uppercase'
                        }}>קארן אבנם</div>
                        <div style={{
                            fontSize: 'var(--font-size-xs)',
                            color: '#938977',
                            fontWeight: 'var(--font-weight-black)',
                            letterSpacing: '0.15em',
                            textTransform: 'uppercase',
                            opacity: 1
                        }}>מערכת ניהול סקרים מתקדמת</div>
                    </div>
                </div>

                <div style={{
                    height: '1px',
                    background: 'linear-gradient(90deg, transparent, rgba(19, 52, 55, 0.1), transparent)',
                    margin: 'var(--space-8) 0'
                }}></div>

                <div style={{ marginBottom: 'var(--space-10)' }}>
                    <h2 style={{
                        fontSize: 'var(--font-size-lg)',
                        fontWeight: 'var(--font-weight-bold)',
                        color: 'var(--primary-600)',
                        marginBottom: 'var(--space-2)',
                        letterSpacing: '-0.01em'
                    }}>התחברות למערכת</h2>
                    <p style={{
                        color: 'var(--neutral-500)',
                        fontSize: 'var(--font-size-sm)',
                        lineHeight: '1.6'
                    }}>אנא התחבר עם חשבון ה-Google המורשה שלך כדי להמשיך ללוח הבקרה</p>
                </div>

                <div style={{ marginBottom: 'var(--space-6)' }}>
                    <button
                        onClick={() => googleLogin()}
                        className="btn-google-premium"
                        disabled={loading}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-1 .67-2.28 1.07-3.71 1.07-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                            <path d="M5.84 14.09c-.22-.67-.35-1.39-.35-2.09s.13-1.42.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                        </svg>
                        התחברות מאובטחת
                    </button>
                </div>

                {loading && (
                    <div style={{
                        color: 'var(--accent-500)',
                        marginBottom: 'var(--space-4)',
                        fontWeight: 'var(--font-weight-semibold)',
                        fontSize: 'var(--font-size-sm)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 'var(--space-2)'
                    }}>
                        <div className="spinner-small" style={{ width: '16px', height: '16px' }}></div>
                        מעבד התחברות...
                    </div>
                )}

                {error && (
                    <div style={{
                        padding: 'var(--space-4)',
                        background: 'var(--error-50)',
                        color: 'var(--error-700)',
                        borderRadius: 'var(--radius-lg)',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        border: '1px solid var(--error-100)',
                        animation: 'fadeIn 0.3s ease-out'
                    }}>
                        {error}
                    </div>
                )}
            </div>

            <div className="login-footer-text">
                © {new Date().getFullYear()} קארן אבנם - כל הזכויות שמורות
            </div>
        </div>
    );
};
